import { $http } from "@/plugins/api";
import type { Commit } from "vuex";
import { GetterTree, MutationTree } from "vuex";
import {
  ApiLicence,
  Collection,
  Data,
  DeleteLicence,
  GraphDataType,
  History,
  IndividualLicence,
  LicenseResponse,
  OrgID,
} from "./type";
import dayjs from "dayjs";

interface LicenceData {
  collection: LicenseResponse | null;
  children: Collection | null;
  data: Data[] | null;
  total: number | null;
  available: number | null;
  history: History[] | null;
  individualLicence: IndividualLicence | null;
  graph: GraphDataType | null;
}

interface LicenceState {
  licence: LicenceData;
}

const getDefaultLicence = (): LicenceState => {
  return {
    licence: {
      data: null,
      total: null,
      available: null,
      history: null,
      individualLicence: null,
      graph: null,
      collection: null,
      children: null,
    },
  };
};
const state: LicenceState = getDefaultLicence();
const getters: GetterTree<LicenceState, Data> = {
  getListOfLicences: (state) => state.licence.data?.slice() || [],
  getListOfLicencesCollection: (state) => state.licence.collection,
  getListOfLicencesByChildren: (state) => state.licence.data?.slice() || [],
  getLicenceHistoryStats: (state) => state.licence.history,
  getLicenceGraph: (state) => state.licence.graph?.data,
};

const actions = {
  async clearLicenceCache({ commit }: { commit: Commit }) {
    commit("resetState");
  },
  async getLicenceHistory({ commit }: { commit: Commit }, { uuid }: OrgID) {
    const today = new Date();
    today.setMonth(today.getMonth() - 12);
    const dateTo = `${new Date().getFullYear()}-${(new Date().getMonth() + 1)
      .toString()
      .padStart(2, "0")}`;
    const prevMonths = `${today.toISOString().slice(0, 7)}`;
    const service = import.meta.env.VITE_PHISH_ORG_SERVICE;
    const res = await $http.get(
      `${
        import.meta.env.VITE_API_CLIENT
      }/v2/licence/organisations/${uuid}/licences/${service}/history/graph?date_from=${prevMonths}&date_to=${dateTo}`,
    );
    commit(`setLicenceGraph`, res.data);
  },
  async getLicenceHistoryByService(
    { commit }: { commit: Commit },
    {
      service_uuid,
    }: {
      service_uuid: string;
    },
  ) {
    const today = new Date();
    today.setMonth(today.getMonth() - 12);
    const dateTo = `${new Date().getFullYear()}-${(new Date().getMonth() + 1)
      .toString()
      .padStart(2, "0")}`;
    const prevMonths = `${today.toISOString().slice(0, 7)}`;
    const res = await $http.get(
      `${import.meta.env.VITE_API_CLIENT}/v2/licence/${service_uuid}/history/graph?date_from=${prevMonths}&date_to=${dateTo}`,
    );
    commit(`setLicenceGraph`, res.data);
  },
  async createLicence(
    { commit }: { commit: Commit },
    {
      solution_uuid,
      uuid,
      formData,
      // TODO: This is temporary fix to not call v1 for SEG licence. Will be removed once v1 is switched off https://gitlab.titanhq.com/titanhq/dev/scrum/team-sherman/-/issues/2000 is done
      shouldCallV1 = false,
    }: ApiLicence & { shouldCallV1: boolean },
  ) {
    const seats_purchased = formData?.seats_purchased
      ? Math.trunc(+formData.seats_purchased)
      : undefined;
    const res = await $http.post(
      `${import.meta.env.VITE_API_CLIENT}/v2/licence`,
      {
        solution_uuid: solution_uuid,
        organisation_uuid: uuid,
        seats_purchased: seats_purchased,
        expires_at: dayjs(new Date()).add(1, "year").format("YYYY-MM-DD"),
      },
    );
    //to be removed after total switch to licence v2
    if (shouldCallV1) {
      await $http.post(
        `${import.meta.env.VITE_API_CLIENT}/v1/licence/organisations/${uuid}/licences`,
        {
          service_uuid: solution_uuid,
          seats_purchased: seats_purchased,
        },
      );
    }

    commit("addLicence", res.data);
  },
  async removeLicence(
    { commit }: { commit: Commit },
    {
      licence_uuid,
      uuid,
      solution_uuid,
      // TODO: This is temporary fix to not call v1 for SEG licence. Will be removed once v1 is switched off https://gitlab.titanhq.com/titanhq/dev/scrum/team-sherman/-/issues/2000 is done
      shouldCallV1 = false,
    }: DeleteLicence & { shouldCallV1: boolean },
  ) {
    const response = await $http.delete(
      `${import.meta.env.VITE_API_CLIENT}/v2/licence/${licence_uuid}`,
    );

    if (shouldCallV1) {
      await $http.delete(
        `${import.meta.env.VITE_API_CLIENT}/v1/licence/organisations/${uuid}/licences/${solution_uuid}`,
      );
    }

    commit("removeAlicence", response.data);
  },
  async updateLicence(
    { commit }: { commit: Commit },
    {
      licence_uuid,
      formData,
      solution_uuid,
      uuid,
      // TODO: This is temporary fix to not call v1 for SEG licence. Will be removed once v1 is switched off https://gitlab.titanhq.com/titanhq/dev/scrum/team-sherman/-/issues/2000 is done
      shouldCallV1 = false,
    }: ApiLicence & { shouldCallV1: boolean },
  ) {
    const seats_purchased = formData?.seats_purchased
      ? Math.trunc(+formData.seats_purchased)
      : undefined;
    const res = await $http.patch(
      `${import.meta.env.VITE_API_CLIENT}/v2/licence/${licence_uuid}`,
      {
        expires_at: dayjs(formData?.expires_at).format("YYYY-MM-DD"),
        seats_purchased: seats_purchased,
      },
    );
    if (shouldCallV1) {
      await $http.patch(
        `${import.meta.env.VITE_API_CLIENT}/v1/licence/organisations/${uuid}/licences/${solution_uuid}`,
        {
          seats_purchased: seats_purchased,
        },
      );
    }

    commit("updateLicences", res.data);
  },
  async listAllLicences({ commit }: { commit: Commit }, { uuid }: OrgID) {
    const response = await $http.get(
      `${import.meta.env.VITE_API_CLIENT}/v2/licence/orgs/${uuid}`,
    );
    commit(`listOfLicence`, response.data);
    return response.data;
  },
};

const mutations: MutationTree<LicenceState> = {
  resetState(state) {
    Object.assign(state, getDefaultLicence());
  },
  listOfLicence: (state, data) => (state.licence.collection = data),
  listOfLicenceChildren: (state, data) => (state.licence.children = data),
  setLicenceGraph: (state, data) => (state.licence.graph = data),
  addLicence: (state, data) => (state.licence.data = data),
  removeAlicence(state, data) {
    state.licence.collection?.data.filter(
      (item) => item.service.uuid !== data.service_uuid,
    );
  },
  updateLicences(state, data) {
    if (state.licence.data) {
      state.licence.data = state.licence.data.map((obj) =>
        obj.uuid === data.uuid ? data : obj,
      );
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
